import { gql, useApolloClient } from '@apollo/client';
import { DateTime } from 'luxon';
import { useCallback, useMemo } from 'react';
// eslint-disable-next-line import/no-internal-modules
import { myEventsCutoff } from '../../routes/events/my-events-cutoff';
// eslint-disable-next-line import/no-internal-modules
import { useEventsStore } from '../../routes/events/store';
import { useSocketFeed } from '../../store';
import type { QuickSwitchEvent, RawQuickSwitchEvent } from './event';
import { isShownQuickSwitchEvent, mapQuickSwitchEvent, QUICK_SWITCH_EVENT_FIELDS, QUICK_SWITCH_FETCH_POLICY } from './event';

const FAVORITE_EVENTS = gql`
  query QuickSwitchFavoriteEvents($input: GetEventsInput!) {
    getEvents(input: $input) {
      result {
        ...QuickSwitchEventFields
      }
    }
  }
  ${QUICK_SWITCH_EVENT_FIELDS}
`;

interface QueryResult {
  getEvents?: {
    result: (RawQuickSwitchEvent | null)[];
  } | null;
}

interface QueryVars {
  input: {
    ids: number[];
    page: number;
    limit: number;
  };
}

// Same 30-minute grace the my-events feed gives ended events.
const ENDED_GRACE_MS = 30 * 60 * 1000;

export const useQuickSwitchFavoriteEvents = (): { events: QuickSwitchEvent[]; loading: boolean } => {
  const client = useApolloClient();
  const favoriteEvents = useEventsStore((v) => v.favoriteEvents);
  const ids = useMemo(() => [...favoriteEvents].map(Number).sort((a, b) => a - b), [favoriteEvents]);

  const fetchFavorites = useCallback(async (): Promise<RawQuickSwitchEvent[]> => {
    const { data } = await client.query<QueryResult, QueryVars>({
      query: FAVORITE_EVENTS,
      // Limit covers every favorite: a fixed page size would silently truncate the set.
      variables: { input: { ids, page: 1, limit: Math.max(ids.length, 1) } },
      fetchPolicy: QUICK_SWITCH_FETCH_POLICY
    });

    return (data.getEvents?.result ?? []).filter(isShownQuickSwitchEvent);
  }, [client, ids]);

  /* The one feed that can tell from the ids alone: only a change to a favorite is worth a request. */
  const affectsFavorites = useCallback(
    (changed: number[]): boolean => changed.some((id) => favoriteEvents.has(String(id))),
    [favoriteEvents]
  );

  const { rows, loading } = useSocketFeed({
    scope: 'quick-switch:favorites',
    cacheKey: ids.join(','),
    enabled: ids.length > 0,
    fetch: fetchFavorites,
    affects: affectsFavorites
  });

  const events = useMemo(() => {
    const endedCutoff = DateTime.now().minus({ milliseconds: ENDED_GRACE_MS });
    const upcomingCutoff = myEventsCutoff();

    return rows
      .map(mapQuickSwitchEvent)
      .filter((event) => {
        // Remembered rows can outlive an unfavorite, so re-check the store.
        if (!favoriteEvents.has(event.eid)) {
          return false;
        }

        // Same 4 AM window the my-events query caps at server-side.
        const start = DateTime.fromISO(event.startTime ?? '');

        if (start.isValid && start > upcomingCutoff) {
          return false;
        }

        if (event.isLive) {
          return true;
        }

        const end = DateTime.fromISO(event.endTime ?? '');

        return !end.isValid || end >= endedCutoff;
      });
  }, [rows, favoriteEvents]);

  return { events, loading };
};
